import { pluralize } from 'inflected';

export const equalsIgnoreCase = (a: unknown, b: unknown): boolean =>
  typeof a === 'string' &&
  typeof b === 'string' &&
  a.trim().toLowerCase() === b.trim().toLowerCase();

export const conditionallyPluralize = (count: number, noun: string): string =>
  count === 1 ? noun : pluralize(noun);

export const conditionallyPluralizeWithCount = (count: number, noun: string): string =>
  `${count} ${conditionallyPluralize(count, noun)}`;

// Formats a whole-dollar amount for display in a text input (e.g. 350000 -> '$350,000'). Sub-$1
// amounts round to whole dollars here too -- they aren't shown with cents.
export const formatWholeUsd = (value: number): string =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

// Strips a formatWholeUsd-formatted string (or any partial input typed over it) back down to a
// whole-dollar number, rounded to the nearest dollar. Returns null for an empty, unparseable, or
// negative field -- a budget can't be negative, so a negative parse is invalid input rather than
// a same-magnitude positive amount.
export const parseWholeUsdOrNull = (formatted: string): number | null => {
  const stripped = formatted.replace(/[^0-9.-]/g, '');
  const parsed = Number(stripped);
  return stripped === '' || !Number.isFinite(parsed) || parsed < 0 ? null : Math.round(parsed);
};

// Same as parseWholeUsdOrNull, but for callers with no "unset" state of their own -- invalid input
// floors to 0 instead of null.
export const parseWholeUsd = (formatted: string): number => parseWholeUsdOrNull(formatted) ?? 0;

// Truncates text to `max` Unicode code points, appending `…` when cut. Operates on code
// points (not UTF-16 code units) so surrogate pairs (emoji, etc.) are never split.
export const truncate = (text: string, max: number): string => {
  const chars = [...text];
  return chars.length > max ? `${chars.slice(0, max).join('')}…` : text;
};

// Two-letter uppercase initials from a display name. First letter of the first two
// whitespace-separated words, or the first two letters of a single word. Returns null when
// there's nothing to pull from — callers can fall through to an email-derived alternative.
export const nameToInitials = (name: string | null | undefined): string | null => {
  const trimmed = name?.trim() ?? '';
  if (trimmed.length === 0) return null;
  const words = trimmed.split(/\s+/).filter(Boolean);
  const raw = words.length >= 2 ? `${words[0][0]}${words[1][0]}` : words[0].slice(0, 2);
  return raw.toUpperCase();
};

// Two-letter uppercase initials from an email address. Splits the local-part on `._-`
// separators (jane.doe → JD, jane_doe → JD, jdoe → JD). Returns '?' for empty input.
export const emailToInitials = (email: string): string => {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const raw = parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : local.slice(0, 2) || '?';
  return raw.toUpperCase();
};

// Turns a user email (e.g. 'jane.doe@x.com') into a display name ('Jane Doe').
// Non-emails are returned unchanged.
export const transformEmailToFullName = (email: string): string => {
  if (email == null || !email.includes('@')) {
    return email ?? '';
  }
  const username = email.split('@')[0];
  const charArray = username.split('');
  for (let i = 0; i < charArray.length; i++) {
    if (i === 0) {
      charArray[i] = charArray[i].toUpperCase();
    } else if (charArray[i - 1] === '.') {
      charArray[i - 1] = ' ';
      charArray[i] = charArray[i].toUpperCase();
    }
  }
  return charArray.join('');
};
